// Eskom PARCC — Compliance Override cascade (owner feedback #9, Aug 2026).
//
// "When the Compliance Override is changed to Compliant, the system should
// automatically update all procedures that are marked as Non-Compliant to
// Compliant in a transaction." A "procedure" is a CES checklist row; each
// non-compliant row is projected into one register Finding carrying the same
// criterion. So the cascade flips BOTH grains to Compliant — the CES rows (the
// source, so a later re-capture cannot regenerate the gap and silently undo the
// cascade) and the Findings (so the register/counts agree immediately).
//
// One-shot at the moment the override is set — NOT a standing invariant: where
// only some gaps are addressed, a coordinator still edits the affected procedures
// individually, and the transaction follows via the normal findings roll-up.
//
// PURE: no React, no Firebase. The write orchestration (dbWrite) and the UI
// confirm consume these predicates/transforms.
import { cesMapStatusToRegister } from './ces';
import { isFindingLevel } from './dashboard';

/** The CES status a cascade sets a non-compliant procedure to. */
export const COMPLIANT_CES_STATUS = 'Compliant';

/**
 * True when a CES row's status is a NON-COMPLIANT verdict (its mapped register
 * level carries a risk weight). Compliant / Not-Applicable / Pending / blank are
 * not — they are left untouched by the cascade.
 */
export function isNonCompliantCesStatus(status: string | undefined): boolean {
  return isFindingLevel(cesMapStatusToRegister(String(status ?? '')));
}

/** Count the non-compliant procedures in a CES row set (for the confirm dialog). */
export function countNonCompliantCes<T extends { status?: string }>(rows: readonly T[]): number {
  return rows.reduce((n, r) => n + (isNonCompliantCesStatus(r.status) ? 1 : 0), 0);
}

/**
 * Flip every NON-COMPLIANT CES row to Compliant, leaving compliant / N/A / pending
 * rows exactly as they are. Returns a new array (input untouched); rows keep every
 * other field. Use for the local-state flip on the CES page and the server rewrite.
 */
export function cesRowsToCompliant<T extends { status?: string }>(rows: readonly T[]): T[] {
  return rows.map((r) =>
    isNonCompliantCesStatus(r.status) ? { ...r, status: COMPLIANT_CES_STATUS } : r,
  );
}

/** The compliance level a cascade sets a non-compliant finding to. */
export const COMPLIANT_FINDING_LEVEL = 'Compliant';

/** A finding's judged level: its final result, else the suggestion. */
function findingLevelOf(f: { finalCompliance?: string | null; suggestedCompliance?: string | null }): string {
  return String(f.finalCompliance ?? f.suggestedCompliance ?? '').trim();
}

/**
 * True when a finding is a genuine NON-COMPLIANT gap (risk-weighted level, not an
 * "Additional comments" note). Compliant / N/A / notes are left as-is.
 */
export function isNonCompliantFinding(f: {
  finalCompliance?: string | null;
  suggestedCompliance?: string | null;
  legacyStatus?: string | null;
}): boolean {
  if (f.legacyStatus === 'Additional comments') return false;
  return isFindingLevel(findingLevelOf(f));
}

/** Count the non-compliant findings for a transaction (for the confirm dialog). */
export function countNonCompliantFindings<
  T extends { finalCompliance?: string | null; suggestedCompliance?: string | null; legacyStatus?: string | null; deleted?: boolean | null },
>(findings: readonly T[]): number {
  return findings.reduce((n, f) => n + (!f.deleted && isNonCompliantFinding(f) ? 1 : 0), 0);
}

/**
 * The lifecycle a cascade / self-declaration stamps when it CLOSES a finding
 * (RTM Workstream I, Sep 2026). Mirrors `Finding.closedVia`.
 */
export interface CascadeClose {
  closedVia: 'buyer-fix' | 'override' | 'self-declaration' | 'recapture';
  closedAt: string;
}

/**
 * CLOSE every non-compliant finding instead of relabelling it Compliant (RTM
 * Workstream I). Marks each risk-weighted, non-note, live finding
 * `effectiveStatus = 'Closed'` with `closedAt` + `closedVia`, KEEPING its
 * compliance level (the tier is declared/resolved, not erased). Compliant /
 * Not-Applicable / note / soft-deleted rows are left exactly as they are. Returns
 * a new array (input untouched); rows keep every other field.
 *
 * Why not relabel to Compliant: relabelling drops the finding out of
 * `genuineFindings` entirely (its level is no longer risk-weighted), so a fixed
 * gap vanished from the register and the "gaps addressed" figures. Closing it
 * keeps it identified AND resolved — a manager still sees the gap was found and
 * that it has been addressed, with the route recorded in `closedVia`.
 */
export function closeNonCompliantFindings<
  T extends {
    finalCompliance?: string | null;
    suggestedCompliance?: string | null;
    legacyStatus?: string | null;
    deleted?: boolean | null;
    effectiveStatus?: string;
    closedAt?: string | null;
    closedVia?: string | null;
  },
>(findings: readonly T[], close: CascadeClose): T[] {
  return findings.map((f) =>
    !f.deleted && isNonCompliantFinding(f)
      ? { ...f, effectiveStatus: 'Closed', closedAt: close.closedAt, closedVia: close.closedVia }
      : f,
  );
}
